import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { StockCodeMaster } from "../../types/assetTypes";
import { getStockCodeColumns } from "../../const/AssertData";
import {
  getStockCodeById,
  getStockCodeMasterDataList,
  saveStockCode,
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { RootState } from "../../store/Reduxstore";

interface StockCodeProps {
  setPathname: (path: string) => void;
}

const StockCode: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<StockCodeMaster[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [StockCodeData, setStockCodeData] = useState<StockCodeMaster>({
    stockCodeId: 0,
    stockCode: "",
    currentStock: 0,
    description: "",

  });

  // Memoized column definition
  const columns = useMemo(() => getStockCodeColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditStockCode(rowData.stockCodeId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" }; // Ensure correct structure
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );
  const handleEditStockCode = async (stockCodeId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getStockCodeById(stockCodeId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setStockCodeData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Generate filters dynamically
  const filters = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        if (col.field) {
          acc[col.field] = {
            value: null,
            matchMode: FilterMatchMode.STARTS_WITH,
          };
        }
        return acc;
      },
      {} as Record<string, { value: any; matchMode: FilterMatchMode }>
    );
  }, [columns]);

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getStockCodeMasterDataList(page, rows, filter ?? filterData);

      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setStockCodeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setStockCodeData({
      stockCodeId: 0,
      stockCode: "",
      currentStock: 0,
      description: "",

    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (StockCodeData.stockCode === "") {
      CustomMessage("Add the stock code", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveStockCode(StockCodeData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        CustomMessage(status.message, "success", enqueueSnackbar);
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Assets List Write"
        ) && (
            <Chip
              className="btn-black-outlined mb-3"
              label="New"
              color="warning"
              onClick={() => setOpenDialog(true)}
              icon={<AddIcon />}
              variant="outlined"
            />
          )}
      </div>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          //filters={filters}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditStockCode(rowData.stockCodeId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add Stock Code"
        visible={openDialog}
        onHide={handleClear}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // 🔒 prevent form submission
            }
          }}
        >
          <div className="mb-3">
            <h6>Stock Code</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={StockCodeData.stockCode}
              name="stockCode"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>
          <div className="mb-3">
            <h6>Current Stock</h6>

            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={StockCodeData.currentStock}
              name="currentStock"
              onChange={handleChange}
              sx={{ mt: 0 }}

            />
          </div>
          <div className="mb-3">
            <h6>Description</h6>

            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={StockCodeData.description}
              name="description"
              onChange={handleChange}
              sx={{ mt: 0 }}

            />
          </div>


          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "3px",
            gap: 3
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"
            >
              Cancel
            </ReusablePrimeButton>
            <ReusablePrimeButton severity="info" onClick={handleSubmit}>
              Submit
            </ReusablePrimeButton>
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default StockCode;
